import { Expose, Transform, Type } from 'class-transformer'
import { Truck } from 'react-feather'
import moment from 'moment'

import { ASN } from './ASN'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { Dock } from './Dock'
import { Driver } from './Driver'
import { Facility } from './Facility'
import { Truckload } from './Truckload'
import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'

enum AppointmentType {
  live = 'Live Load',
  drop = 'Drop Trailer',
}

export enum AppointmentStatus {
  scheduled = 'Scheduled',
  arrived = 'Arrived',
  docked = 'Docked',
  completed = 'Completed',
}

export class Appointment {
  static type = 'appointment'
  static typeDisplay = 'Appointment'
  static icon = Truck

  static AppointmentType = AppointmentType

  type = Appointment.type
  typeDisplay = Appointment.typeDisplay
  icon = Appointment.icon

  guid: string = ''

  @Type(() => Client)
  client: Client = new Client()

  @Type(() => Facility)
  facility: Facility = new Facility()

  @Expose({ name: 'appointment_type' })
  appointmentType?: AppointmentType

  @Transform((value) => (value ? moment(value) : value))
  start?: moment.Moment

  @Transform((value) => (value ? moment(value) : value))
  end?: moment.Moment

  @Type(() => ASN)
  asn?: ASN

  @Type(() => Truckload)
  truckload?: Truckload

  @Type(() => Carrier)
  broker?: Carrier

  @Type(() => Dock)
  dock?: Dock

  @Type(() => Driver)
  driver?: Driver

  @Expose({ name: 'internal_notes' })
  internalNotes: string = ''

  @Expose({ name: 'arrived_on' })
  @Transform((value) => (value ? moment(value) : value))
  arrivedOn?: moment.Moment

  @Expose({ name: 'docked_on' })
  @Transform((value) => (value ? moment(value) : value))
  dockedOn?: moment.Moment

  @Expose({ name: 'departed_on' })
  @Transform((value) => (value ? moment(value) : value))
  departedOn?: moment.Moment

  @Expose({ name: 'created_on' })
  @Transform((value) => (value ? moment(value) : value))
  createdOn: moment.Moment = moment()

  @Expose({ name: 'updated_on' })
  @Transform((value) => (value ? moment(value) : value))
  updatedOn: moment.Moment = moment()

  get name() {
    return `Appointment for ${this.asn?.name || this.truckload?.name}`
  }

  get workflow(): WorkflowSequence {
    return [
      [AppointmentStatus.scheduled, this.createdOn],
      [AppointmentStatus.arrived, this.arrivedOn],
      [AppointmentStatus.docked, this.dockedOn],
      [AppointmentStatus.completed, this.departedOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [AppointmentStatus.scheduled]: {
        forward: [
          {
            label: 'Driver Arrived',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'arrived').then(() => onAction?.())
            },
          },
        ],
      },
      [AppointmentStatus.arrived]: {
        forward: [
          {
            label: 'Truck Docked',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'docked').then(() => onAction?.())
            },
          },
        ],
        backward: [
          {
            label: 'Undo Arrival',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'unarrived').then(() => onAction?.())
            },
          },
        ],
      },
      [AppointmentStatus.docked]: {
        forward: [
          {
            label: 'Appointment Completed',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'completed').then(() => onAction?.())
            },
          },
        ],
        backward: [
          {
            label: 'Undo Docking',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'undocked').then(() => onAction?.())
            },
          },
        ],
      },
      [AppointmentStatus.completed]: {
        backward: [
          {
            label: 'Undo Completion',
            onClick: () => {
              skuit.sc.appointments.action(this.guid, 'uncompleted').then(() => onAction?.())
            },
          },
        ],
      },
    }
  }

  get status() {
    if (!this.arrivedOn) {
      return AppointmentStatus.scheduled
    } else if (!this.dockedOn) {
      return AppointmentStatus.arrived
    } else if (!this.departedOn) {
      return AppointmentStatus.docked
    } else if (this.departedOn) {
      return AppointmentStatus.completed
    }
    return ''
  }
}
